import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useActiveProject } from '../../context/ActiveProjectContext';
import { useAuth } from '../../context/AuthContext';
import {
  useSidebar,
  SIDEBAR_MODE,
  getSidebarMinimizeLabel,
} from '../../context/SidebarContext';
import { useMenuAccess } from '../../context/MenuAccessContext';
import { useAccessManifest } from '../../context/AccessManifestContext';
import { useCompanyProjects } from '../../context/CompanyProjectsContext';
import { useProgressiveNavContext } from '../../hooks/useProgressiveNavContext';
import { useProjectActivator, useAssignedProject } from '../../hooks/useCompanyPersonnelQueries';
import { TOUR_OPEN_MOBILE_MENU_EVENT } from '../onboarding/tourUtils';
import { useProgressiveMenuAccess } from './navigation/progressiveNav';
import { useChatNotifications } from '../../context/ChatNotificationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/Axiom-Logo.png';
import { GoSidebarCollapse } from 'react-icons/go';

import MyProfileMenu from '../profile/MyProfileMenu';
import MobileMetroMenu from './MobileMetroMenu';
import DesktopSidebarMenuShell from './navigation/DesktopSidebarMenuShell';
import { createSidebarMenuComponents } from './navigation/sidebarMenuPrimitives';
import NavigationBarModals from './navigation/NavigationBarModals';
import NavigationBarMobileChrome from './navigation/NavigationBarMobileChrome';
import { getDesktopActiveMenuId, getRouteMenuId } from './navigation/menuRoutingConfig';
import { getAdminSidebarExpansionsForRoute } from './navigation/adminNavConfig';
import { mergeRouteExpansions, toggleExpandedMenu } from './navigation/sidebarMenuState';
import notify from '../ui/feedback/notify';
import { api, getImageFetchUrl, getUploadUrl } from '../../api';
import { HiMenuAlt3 } from 'react-icons/hi';
import { useIsMobileLayout } from '../../hooks/useMediaQuery';
import useApprovedBudgets from '../budget/shared/hooks/useApprovedBudgets';
import { useProjectHasApprovedBudget } from '../../hooks/useProjectHasApprovedBudget';

const NavigationBar = () => {
  const [showAbout, setShowAbout] = useState(false);
  const [showNoProjectModal, setShowNoProjectModal] = useState(false);
  const [showProjectFormModal, setShowProjectFormModal] = useState(false);
  const [showDailyTypeModal, setShowDailyTypeModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const isMobile = useIsMobileLayout();
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' && navigator.onLine);
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    const openMobileMenuForTour = () => setShowMobileMenu(true);
    window.addEventListener(TOUR_OPEN_MOBILE_MENU_EVENT, openMobileMenuForTour);
    return () => window.removeEventListener(TOUR_OPEN_MOBILE_MENU_EVENT, openMobileMenuForTour);
  }, []);

  // Menu access control - now using shared context
  const {
    hasMenuAccess: baseHasMenuAccess,
    hasMenuAccessOrChild: baseHasMenuAccessOrChild,
    menusLoaded,
  } = useMenuAccess();
  const { isDemoProjectBootstrap } = useProgressiveNavContext();
  const {
    showCompanyMenu,
    hasMenuAccess,
    hasMenuAccessOrChild,
  } = useProgressiveMenuAccess({
    isDemoProjectBootstrap,
    hasMenuAccess: baseHasMenuAccess,
    hasMenuAccessOrChild: baseHasMenuAccessOrChild,
  });
  const { manifest } = useAccessManifest();
  const isPlatformDeveloper = manifest?.platformDeveloper === true;
  
  // Company projects availability
  const { hasProjects, configuredProjects, refreshProjectCount } = useCompanyProjects();
  const { hasUnreadOpsChat } = useChatNotifications();
  
  // Sidebar state from context
  const {
    collapsed,
    setCollapsed,
    sidebarHidden,
    sidebarMode,
    minimizeSidebar,
    revealSidebar,
    expandSidebar,
    toggleSidebarExpanded,
  } = useSidebar();
  const [expandedMenus, setExpandedMenus] = useState({});

  const handleAboutOpen = () => setShowAbout(true);
  const handleAboutClose = () => setShowAbout(false);
  const handleNoProjectClose = () => setShowNoProjectModal(false);
  const { activeProject, setActiveProject } = useActiveProject();
  const { approvedPresence } = useApprovedBudgets(activeProject?.project_id);
  const hasApprovedBudget = useProjectHasApprovedBudget(activeProject?.project_id);
  const auth = useAuth();
  const activateProject = useProjectActivator();
  const navigate = useNavigate();
  const location = useLocation();

  const sessionTenant =
    typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null;
  const { data: assignedProject } = useAssignedProject({
    enabled: menusLoaded && !!auth.user && !!auth.token && !activeProject && !!sessionTenant,
  });

  const activeMenuId = getDesktopActiveMenuId(location.pathname, location.search);

  const toggleMenu = useCallback((menuId, { keepOpenIds = [] } = {}) => {
    if (collapsed && !sidebarHidden) {
      expandSidebar();
    }
    setExpandedMenus((prev) => toggleExpandedMenu(prev, menuId, keepOpenIds));
  }, [collapsed, sidebarHidden, expandSidebar]);

  useEffect(() => {
    if (isMobile) return undefined;

    const routeMenuId = getRouteMenuId(location.pathname, location.search);
    const nestedExpansions = getAdminSidebarExpansionsForRoute(routeMenuId);
    const desktopActiveId = getDesktopActiveMenuId(location.pathname, location.search);
    const expansions = nestedExpansions || (desktopActiveId ? { [desktopActiveId]: true } : null);

    if (!expansions) return undefined;

    setExpandedMenus((prev) => mergeRouteExpansions(prev, expansions));
  }, [location.pathname, location.search, isMobile]);

  // Menu access is now handled by MenuAccessContext

  const handleSummarySelect = (area) => {
    setShowSummaryModal(false);
    navigate(`/summary/${area}`);
  };

  const handleLogout = () => {
    auth.logout();
    // Redirect to landing page (not login) to show proper background
    navigate('/');
    setProfileMenuOpen(false);
  };

  const handleUploadAvatar = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/users/me/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const updatedUser = response?.data;
      if (updatedUser?.photo_url) {
        setAvatarUrl(updatedUser.photo_url);
      }
      if (auth.setUser) {
        auth.setUser(updatedUser);
      }
      return updatedUser?.photo_url || avatarUrl;
    } catch (error) {
      const message = error?.response?.data?.detail || 'Upload failed. Please try again.';
      throw new Error(message);
    }
  };

  const handleProfileAction = (action) => {
    switch (action) {
      case 'logout':
        handleLogout();
        break;
      case 'my-profile':
        navigate('/profile');
        break;
      case 'company-profile':
        navigate('/settings/company');
        break;
      default:
        setProfileMenuOpen(false);
    }
    if (action !== 'logout') {
      setProfileMenuOpen(false);
    }
  };

  const openProfileMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const x = rect.left + (collapsed ? rect.width + 8 : rect.width / 2);
    setProfileMenuPos({ x, rectTop: rect.top, rectBottom: rect.bottom });
    setProfileMenuOpen(true);
  };

  const closeProfileMenu = () => setProfileMenuOpen(false);

  const handleSaveProject = async (projectData, projectId) => {
    const method = projectId ? 'put' : 'post';
    const url = projectId ? `/projects/${projectId}` : '/projects';

    try {
      const response = await api[method](url, projectData);
      setShowProjectFormModal(false);
      if (!projectId) {
        await refreshProjectCount();
        navigate('/projects/portfolio', { state: { newlyCreatedProject: response.data } });
      }
    } catch (err) {
      console.error('Error saving project:', err);
      notify.error('Failed to save project. Please check the data.');
    }
  };

  const handleDailyTypeSelect = (dailyType) => {
    setShowDailyTypeModal(false);
    switch (dailyType) {
      case 'survey': navigate('/dailies/survey/new'); break;
      case 'drilling': navigate('/dailies/drilling/new'); break;
      case 'recording': navigate('/dailies/recording/new'); break;
      case 'management': navigate('/dailies/management/new'); break;
      default: navigate('/dailies/new');
    }
  };

  const isAdmin = auth.user && (auth.user.role === 'admin' || auth.user.role === 'superadmin');
  const isSuperAdmin = auth.user && auth.user.role === 'superadmin';
  const projectSelectorRef = useRef(null);

  const handleQuickActivateProject = useCallback(async (project) => {
    if (activeProject?.project_id === project.project_id) {
      setShowProjectSelector(false);
      return;
    }
    try {
      const data = await activateProject(project.project_id);
      setActiveProject(data);
      setShowProjectSelector(false);
    } catch (err) {
      console.error('Error activating project:', err);
    }
  }, [activeProject, setActiveProject, activateProject]);

  useEffect(() => {
    if (!showProjectSelector) return;
    const handleClickOutside = (e) => {
      if (projectSelectorRef.current && !projectSelectorRef.current.contains(e.target)) {
        setShowProjectSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProjectSelector]);

  // Service visibility flags - only show menus for services enabled in Project Scope Configuration
  const hasSurvey = activeProject?.has_survey === true;
  const hasDrilling = activeProject?.has_drilling === true;
  const hasRecording = activeProject?.has_recording_camp === true;
  const hasBaseCamp = activeProject?.has_base_camp === true;
  /** Project Scope — "Helicopter Support"; hide all helicopter ops menu entries when off */
  const showHelicopterOpsMenu = activeProject?.has_helicopter === true;

  const [profileMenuPos, setProfileMenuPos] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  // Avatar URL: on CRA dev (port 3000) use /api/v1/serve-upload/... so the proxy serves files from UPLOADS_PATH
  const getFullAvatarUrl = (photoUrl) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http')) return photoUrl;
    const devCra =
      typeof window !== 'undefined' &&
      String(window.location.port) === '3000' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    if (devCra && photoUrl.startsWith('/uploads/')) {
      return getImageFetchUrl(photoUrl);
    }
    return getUploadUrl(photoUrl);
  };

  const [avatarUrl, setAvatarUrl] = useState(getFullAvatarUrl(auth.user?.photo_url));

  useEffect(() => {
    setAvatarUrl(getFullAvatarUrl(auth.user?.photo_url));
  }, [auth.user?.photo_url]);

  // Bootstrap assigned project only when nothing is selected — never override manual activation.
  useEffect(() => {
    const pid = assignedProject?.project_id;
    if (!pid || activeProject) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const data = await activateProject(pid);
        if (!cancelled) setActiveProject(data);
      } catch (err) {
        console.error('Error loading assigned project:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [assignedProject?.project_id, activeProject, activateProject, setActiveProject]);

  // Hydrate cached active project when localStorage snapshot is missing scope fields.
  useEffect(() => {
    const pid = activeProject?.project_id;
    if (!pid || activeProject?.start_date) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const data = await activateProject(pid);
        if (!cancelled) setActiveProject(data);
      } catch (err) {
        console.error('Error hydrating active project:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeProject?.project_id, activeProject?.start_date, activateProject, setActiveProject]);

  const sidebarStyle = {
    width: sidebarHidden ? '0' : (collapsed ? '60px' : '234px'),
    minWidth: sidebarHidden ? '0' : (collapsed ? '60px' : '234px'),
    flex: sidebarHidden ? '0 0 0' : `0 0 ${collapsed ? '60px' : '234px'}`,
    height: '100vh',
    backgroundColor: 'var(--archon-navy-deep)',
    color: 'var(--archon-white)',
    transition: 'width 0.3s ease, min-width 0.3s ease, opacity 0.2s ease',
    opacity: sidebarHidden ? 0 : 1,
    visibility: sidebarHidden ? 'hidden' : 'visible',
    display: isMobile ? 'none' : 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    overflowX: 'hidden',
    zIndex: 1000,
    borderRight: sidebarHidden ? 'none' : '1px solid rgba(255,255,255,0.1)',
    pointerEvents: sidebarHidden ? 'none' : 'auto',
  };

  const scrollAreaStyle = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBottom: '8px',
    scrollbarWidth: 'thin',
    scrollbarGutter: 'stable'
  };

  const { renderCollapsedSidebarAction } = createSidebarMenuComponents({
    collapsed,
    expandedMenus,
    activeMenuId,
    toggleMenu,
    setCollapsed,
    setExpandedMenus,
    location,
  });

  if (!auth.user) return null;

  return (
    <>
      {!isMobile && sidebarHidden && (
        <OverlayTrigger
          placement="right"
          overlay={<Tooltip id="tt-show-nav">Show navigation menu (Ctrl+B)</Tooltip>}
        >
          <Button
            type="button"
            variant="dark"
            size="sm"
            className="shadow-sm border border-secondary"
            style={{
              position: 'fixed',
              left: 10,
              top: 12,
              zIndex: 1100,
            }}
            onClick={revealSidebar}
            aria-label="Show navigation menu"
          >
            <HiMenuAlt3 size={20} aria-hidden />
          </Button>
        </OverlayTrigger>
      )}
      <div style={sidebarStyle} aria-hidden={sidebarHidden}>
        {/* Header / Logo */}
        <div 
          className={`d-flex align-items-center p-3 border-bottom border-secondary ${collapsed ? 'justify-content-center' : 'justify-content-start'}`}
          style={{ height: '60px', cursor: 'pointer' }}
          onClick={toggleSidebarExpanded}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
           <img src={logo} alt="Logo" style={{ height: '30px', width: 'auto' }} />
           {!collapsed && (
             <span className="ms-2 text-white" style={{ fontFamily: '"Century Gothic", sans-serif', fontSize: '1.2rem', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
               ArchonPM
             </span>
           )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            PROJECT QUICK SELECTOR
            ═══════════════════════════════════════════════════════════════════ */}
        {menusLoaded && hasProjects && configuredProjects.length > 0 && hasMenuAccess('/projects') && (
          <div ref={projectSelectorRef} data-tour="project-selector" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', position: 'relative' }}>
            {collapsed ? (
              <OverlayTrigger 
                placement="right" 
                overlay={<Tooltip>{activeProject ? activeProject.project_name : 'Select Project'}</Tooltip>}
                container={document.body}
              >
                <div
                  className="d-flex align-items-center justify-content-center py-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => { expandSidebar(); setShowProjectSelector(true); }}
                >
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '6px',
                    backgroundColor: activeProject ? 'rgba(43,88,179,0.3)' : 'rgba(255,255,255,0.1)',
                    border: activeProject ? '1px solid rgba(43,88,179,0.6)' : '1px solid rgba(255,255,255,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.85rem', color: activeProject ? 'var(--archon-nav-active-text)' : 'rgba(255,255,255,0.5)'
                  }}>
                    <i className={`bi ${activeProject ? 'bi-folder-check' : 'bi-folder'}`}></i>
                  </div>
                </div>
              </OverlayTrigger>
            ) : (
              <div className="px-3 py-2">
                <div
                  onClick={() => setShowProjectSelector(!showProjectSelector)}
                  style={{
                    cursor: 'pointer', borderRadius: '6px', padding: '6px 10px',
                    backgroundColor: activeProject ? 'rgba(43,88,179,0.2)' : 'rgba(255,255,255,0.05)',
                    border: activeProject ? '1px solid rgba(43,88,179,0.5)' : '1px solid rgba(255,255,255,0.15)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div className="d-flex align-items-center">
                    <i className={`bi ${activeProject ? 'bi-folder-check' : 'bi-folder'} me-2`}
                       style={{ color: activeProject ? 'var(--archon-nav-active-text)' : 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}></i>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'rgba(255,255,255,0.45)', lineHeight: 1 }}>
                        Active Project
                      </div>
                      <div style={{ fontSize: '0.8rem', color: activeProject ? 'var(--archon-accent)' : 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: activeProject ? 600 : 400 }}>
                        {activeProject ? activeProject.project_name : 'No project selected'}
                      </div>
                    </div>
                    <i className={`bi bi-chevron-${showProjectSelector ? 'up' : 'down'}`}
                       style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}></i>
                  </div>
                </div>

                {showProjectSelector && (
                  <div style={{
                    marginTop: '4px', borderRadius: '6px', overflow: 'hidden',
                    backgroundColor: 'var(--archon-nav-dropdown-bg)', border: '1px solid rgba(255,255,255,0.12)',
                    maxHeight: '200px', overflowY: 'auto',
                    scrollbarWidth: 'thin'
                  }}>
                    {configuredProjects.map(p => {
                      const isActive = activeProject?.project_id === p.project_id;
                      return (
                        <div
                          key={p.project_id}
                          onClick={() => handleQuickActivateProject(p)}
                          style={{
                            padding: '7px 10px', cursor: 'pointer', fontSize: '0.8rem',
                            backgroundColor: isActive ? 'rgba(43,88,179,0.2)' : 'transparent',
                            color: isActive ? 'var(--archon-accent)' : 'rgba(255,255,255,0.7)',
                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                            display: 'flex', alignItems: 'center', gap: '8px',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)'; }}
                          onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <i className={`bi ${isActive ? 'bi-check-circle-fill' : 'bi-circle'}`}
                             style={{ fontSize: '0.7rem', flexShrink: 0, color: isActive ? 'var(--archon-nav-active-text)' : undefined }}></i>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isActive ? 600 : 400 }}>
                            {p.project_name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {menusLoaded && hasMenuAccess('/projects') && !hasProjects && !isMobile && (
          <div
            data-tour="project-selector"
            className="px-3 py-2"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem' }}
          >
            <i className="bi bi-folder me-2" aria-hidden />
            No project selected
          </div>
        )}

        <div style={scrollAreaStyle}>
          <DesktopSidebarMenuShell
            collapsed={collapsed}
            expandedMenus={expandedMenus}
            activeMenuId={activeMenuId}
            toggleMenu={toggleMenu}
            setCollapsed={setCollapsed}
            setExpandedMenus={setExpandedMenus}
            hasMenuAccess={hasMenuAccess}
            hasMenuAccessOrChild={hasMenuAccessOrChild}
            showCompanyMenu={showCompanyMenu}
            hasProjects={hasProjects}
            activeProject={activeProject}
            hasApprovedBudget={hasApprovedBudget}
            approvedPresence={approvedPresence}
            isAdmin={isAdmin}
            isSuperAdmin={isSuperAdmin}
            isPlatformDeveloper={isPlatformDeveloper}
            isDemoProjectBootstrap={isDemoProjectBootstrap}
            hasSurvey={hasSurvey}
            hasDrilling={hasDrilling}
            hasRecording={hasRecording}
            hasBaseCamp={hasBaseCamp}
            showHelicopterOpsMenu={showHelicopterOpsMenu}
            online={online}
            menusLoaded={menusLoaded}
            user={auth.user}
            onNewProject={() => setShowProjectFormModal(true)}
            onContactSupport={() => setShowContactSupport(true)}
            onAboutOpen={handleAboutOpen}
            accessManifest={manifest}
          />
        </div>

          {/* Progressive sidebar: collapse (expanded) or expand + hide (collapsed) */}
          <div className="px-3 py-2 border-top border-secondary" style={{ flexShrink: 0 }} data-tour="sidebar-controls">
              {sidebarMode === SIDEBAR_MODE.COLLAPSED ? (
                <div className="d-flex align-items-center justify-content-center" style={{ gap: '4px' }}>
                  {renderCollapsedSidebarAction('expand', {
                    onClick: expandSidebar,
                    tooltip: 'Expand sidebar (Ctrl+B)',
                    ariaLabel: 'Expand sidebar',
                    icon: <i className="bi bi-chevron-double-right" style={{ fontSize: '1.1rem' }} aria-hidden />,
                  })}
                  {renderCollapsedSidebarAction('hide', {
                    onClick: minimizeSidebar,
                    tooltip: 'Hide sidebar (Ctrl+Shift+B)',
                    ariaLabel: 'Hide sidebar',
                    icon: <i className="bi bi-chevron-double-left" style={{ fontSize: '1.1rem' }} aria-hidden />,
                  })}
                </div>
              ) : (
                <div
                    className="d-flex align-items-center"
                    style={{ cursor: 'pointer', color: 'var(--archon-nav-icon-muted)' }}
                    onClick={minimizeSidebar}
                    title="Collapse sidebar (Ctrl+B)"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        minimizeSidebar();
                      }
                    }}
                >
                     <div style={{ minWidth: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <GoSidebarCollapse style={{ fontSize: '1.2rem', transition: 'transform 0.3s ease' }} />
                     </div>
                     <span className="ms-3">{getSidebarMinimizeLabel(sidebarMode)}</span>
                </div>
              )}
          </div>

          {/* User Profile Footer */}
          <div className="border-top border-secondary p-3" data-tour="user-profile" style={{ height: '60px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <div
            className="d-flex align-items-center text-white text-decoration-none"
            style={{ cursor: 'pointer', width: '100%' }}
            onClick={openProfileMenu}
            onContextMenu={openProfileMenu}
          >
            <div
              className="rounded-circle bg-secondary d-flex align-items-center justify-content-center"
              style={{ width: '32px', height: '32px', minWidth: '32px', overflow: 'hidden' }}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="avatar"
                  style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                  onError={(e) => {
                    // If image fails to load, hide it and show initial
                    e.target.style.display = 'none';
                  }}
                />
              ) : null}
              <span 
                className="fw-bold" 
                style={{ display: avatarUrl ? 'none' : 'inline' }}
              >
                {auth.user.username.charAt(0).toUpperCase()}
              </span>
            </div>
            {!collapsed && (
              <div className="ms-2 overflow-hidden" style={{ lineHeight: 1.2 }}>
                <div className="text-truncate">{auth.user.full_name || auth.user.username}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <MyProfileMenu
        user={auth.user}
        position={profileMenuOpen ? profileMenuPos : null}
        avatarUrl={avatarUrl}
        onClose={closeProfileMenu}
        onAction={handleProfileAction}
        onUploadAvatar={handleUploadAvatar}
      />

      <NavigationBarModals
        showAbout={showAbout}
        onAboutClose={handleAboutClose}
        showNoProjectModal={showNoProjectModal}
        onNoProjectClose={handleNoProjectClose}
        showProjectFormModal={showProjectFormModal}
        onHideProjectForm={() => setShowProjectFormModal(false)}
        onSaveProject={handleSaveProject}
        showDailyTypeModal={showDailyTypeModal}
        onHideDailyTypeModal={() => setShowDailyTypeModal(false)}
        onDailyTypeSelect={handleDailyTypeSelect}
        showSummaryModal={showSummaryModal}
        onHideSummaryModal={() => setShowSummaryModal(false)}
        onSummarySelect={handleSummarySelect}
        showContactSupport={showContactSupport}
        onHideContactSupport={() => setShowContactSupport(false)}
      />

      {isMobile && (
        <NavigationBarMobileChrome
          activeMenuId={activeMenuId}
          activeProject={activeProject}
          hasUnreadOpsChat={hasUnreadOpsChat}
          onOpenMenu={() => setShowMobileMenu(true)}
        />
      )}

      {/* Mobile Metro Menu */}
      <MobileMetroMenu 
        isOpen={showMobileMenu} 
        onClose={() => setShowMobileMenu(false)}
      />
    </>
  );
};

export default NavigationBar;